/**
 * DIE SICHERUNG (.332): Liste, Download, „Jetzt sichern", Stand der QNAP.
 *
 * ORIGINALTEXT MARVIN (07.10.2026)
 *   „eine backupfunktion, die alles zb. daten/benutzer/rechte/termine usw.
 *    sichert, zum einen manuell auch für als download, und automatisch,
 *    upload auf die qnap." … „dann nur die datenbank, die daten brauchen wir
 *    erstmal nicht."
 *
 * Gesichert wird die ganze Datenbank (Aufträge, Termine, Benutzer, Rechte,
 * Konfiguration, alles) im Format von pg_dump; der Dienst `sicherung` macht
 * das jede Nacht um 2:15 und auf Knopfdruck. Die Dateiablage (11 GB) bleibt
 * auf dem Server.
 */
import { useEffect, useState } from 'react';
import { api, zeitpunkt } from '../api.ts';
import { Karte } from './Geruest.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';

const mb = (b: number) => `${(b / 1024 / 1024).toFixed(1)} MB`;

export function Sicherung() {
  const [d, setD] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const laden = () => api.sicherungen().then(setD).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  /* Solange eine Sicherung angefordert ist, wird nachgesehen: der Dienst
     braucht 20 Sekunden bis zum Start und ein paar für den Upload. */
  useEffect(() => {
    if (!d?.angefordert && !d?.lauf && !laeuft) return;
    const t = setInterval(laden, 3000);
    return () => clearInterval(t);
  }, [d?.angefordert, d?.lauf, laeuft]);

  async function jetzt() {
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const r = await api.sicherungJetzt();
      setHinweis(r.hinweis ?? 'Angefordert.');
      await laden();
      setTimeout(() => setLaeuft(false), 15000);
    } catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
  }

  const stand = d?.stand;
  const ab = d?.ablage;
  const restDateien = ab ? Math.max(0, ab.alle - ab.auf_nas) : 0;
  const restGb = ab ? Math.max(0, (ab.alle_mb - ab.auf_nas_mb) / 1024) : 0;
  /* Wie lange es noch dauert, aus dem Durchsatz der letzten Stunde. */
  const restStunden = ab && ab.je_stunde > 0 ? restDateien / ab.je_stunde : null;
  const dauer = (h: number) => h < 1 ? `rund ${Math.max(5, Math.round(h * 60 / 5) * 5)} Minuten` : `rund ${h < 10 ? h.toFixed(1).replace('.', ',') : Math.round(h)} Stunden`;
  return (
    /* .613: EINE SICHERUNG, ZWEI TEILE, EIN KNOPF. Marvin, 03.11.2026: „Ich will nicht, dass das Ganze Notfallpaket
       heißt ... Ganz normal Sicherung. Und die Frage ist, wo sind jetzt die kompletten Daten, dass ich auch das
       komplette Backup mit allen Daten anstoßen kann ... Wo ist der Prozess dazu?" Teil 1 ist die Datenbank (mit
       Update, Einstellungen, Wiederherstellungsskript), Teil 2 sind alle Dateien. „Jetzt komplett sichern" sichert die
       Datenbank sofort; die Dateien gleicht n8n alle 2 Minuten ab, bis alle auf der QNAP liegen, danach nur Neues. */
    <Karte titel="Sicherung">
      <p className="klein leise" style={{ marginTop: 0 }}>
        Die Sicherung hat zwei Teile, beide landen auf der QNAP im Ordner „MDS FTTH Sicherung“:
        <b> 1. Datenbank</b> (Aufträge, Termine, Benutzer, Rechte, Konfiguration, dazu das zuletzt eingespielte Update, die Einstellungen des Servers und das Skript zum Wiederherstellen), jede Nacht um 2:15 und auf Knopfdruck.
        <b> 2. Dateien</b> (Fotos, Bescheide, Bauakten, alle Anhänge), laufend: was noch nicht auf der QNAP liegt, holt n8n alle 2 Minuten nach.
      </p>
      <Fehlerkasten lage={fehler} />
      {d && !d.da && (
        <div className="hinweis warnung">
          Der Sicherungsordner <code>{d.ordner}</code> ist für die Plattform nicht erreichbar. Im Betrieb liegt er unter
          /opt/mds/sicherung; die docker-compose.yml dieser Lieferung hängt ihn an api und sicherung. Nach dem Einspielen einmal <code>docker compose up -d</code>.
        </div>
      )}
      {hinweis && <div className="hinweis erledigt">{hinweis}</div>}
      <div className="reihe" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" className="knopf haupt" onClick={jetzt} disabled={laeuft || !d?.da || d?.angefordert || !!d?.lauf}>
          {d?.lauf ? 'Sicherung läuft …' : d?.angefordert || laeuft ? 'Angefordert …' : 'Jetzt komplett sichern'}
        </button>
        <span className="klein leise">Datenbank sofort; die Dateien laufen ohnehin, bis alle auf der QNAP liegen.</span>
      </div>

      {d?.da && (
        <div className="sicherung-stand" style={{ marginTop: 10 }}>
          <b>1. Datenbank</b>
          {d.dienst_haengt && <div className="hinweis warnung"><b>Der Dienst „sicherung“ nimmt den Auftrag nicht an</b> (angefordert {zeitpunkt(d.angefordert_am)}, normal startet er nach höchstens 10 Sekunden). Auf dem Server: <code>cd /opt/mds && docker compose up -d sicherung</code>, dann <code>docker compose logs --tail 30 sicherung</code>.</div>}
          {!d.dienst_haengt && d.angefordert && <div className="hinweis">Angefordert {zeitpunkt(d.angefordert_am)}, der Dienst beginnt in wenigen Sekunden.</div>}
          {d.lauf && <div className="hinweis"><b>Läuft seit {zeitpunkt(d.lauf.begonnen)}:</b> {d.lauf.schritt}</div>}
          {stand && !d.lauf && (
            <div className={'hinweis ' + (stand.fehler ? 'warnung' : d.paket_auf_nas ? 'erledigt' : '')}>
              <b>Letzter Lauf {zeitpunkt(stand.zuletzt)}{stand.anlass ? `, ${stand.anlass}` : ''}: {stand.fehler ? 'Fehler' : 'gelungen'}</b>
              <div className="klein">
                {stand.fehler ? stand.fehler : <>{stand.paket}{stand.paket_groesse ? ` (${mb(Number(stand.paket_groesse))})` : ''}, darin die Datenbank {stand.groesse ? `(${mb(Number(stand.groesse))})` : ''}</>}
                {stand.fehlt ? <b>, es fehlt:{stand.fehlt}</b> : null}
              </div>
              {!stand.fehler && <div className="klein">QNAP: {d.paket_auf_nas ? <b>abgelegt {zeitpunkt(d.nas?.zuletzt_ok)}</b> : d.nas && d.nas.ok === false && d.nas.name === stand.paket ? <b>NICHT abgelegt: {d.nas.fehler ?? 'ohne Angabe'}</b> : 'wird in den nächsten Minuten abgeholt (n8n alle 5 Minuten)'}</div>}
            </div>
          )}
          {!stand && !d.lauf && !d.angefordert && <div className="klein leise">Noch keine Sicherung. Die nächste entsteht um 2:15 oder jetzt auf Knopfdruck.</div>}

          <div style={{ marginTop: 10 }}><b>2. Dateien</b></div>
          {ab && (
            <div className={'hinweis ' + (ab.alle > 0 && restDateien === 0 ? 'erledigt' : ab.fehler_stunde > 0 && !ab.je_stunde ? 'warnung' : '')}>
              <b>{ab.auf_nas} von {ab.alle} Dateien auf der QNAP</b> ({(ab.auf_nas_mb / 1024).toFixed(1).replace('.', ',')} von {(ab.alle_mb / 1024).toFixed(1).replace('.', ',')} GB)
              {ab.alle > 0 && <div className="reihe" style={{ height: 8, background: 'var(--rand)', borderRadius: 4, margin: '6px 0', overflow: 'hidden' }}>
                <div style={{ width: `${Math.round(100 * ab.auf_nas / Math.max(1, ab.alle))}%`, background: 'var(--erledigt, #0f6e56)' }} />
              </div>}
              <div className="klein">
                {restDateien === 0 && ab.alle > 0 ? 'Vollständig. Neue Dateien kommen innerhalb von 2 Minuten dazu.'
                  : <>Es fehlen noch {restDateien} Dateien ({restGb.toFixed(1).replace('.', ',')} GB).{ab.je_stunde > 0 ? ` In der letzten Stunde ${ab.je_stunde} Dateien (${(ab.je_stunde_mb / 1024).toFixed(1).replace('.', ',')} GB), fertig in ${dauer(restStunden ?? 0)}.` : ' In der letzten Stunde kam nichts an.'}</>}
                {ab.zuletzt ? ` Zuletzt abgelegt ${zeitpunkt(ab.zuletzt)}.` : ''}
              </div>
              {ab.fehler_stunde > 0 && <div className="klein"><b>{ab.fehler_stunde} Dateien in der letzten Stunde gescheitert</b> (werden wiederholt){ab.letzter_fehler ? `: ${ab.letzter_fehler.fehler} (${zeitpunkt(ab.letzter_fehler.am)})` : ''}</div>}
            </div>
          )}

          {!!d.protokoll?.length && (
            <details style={{ marginTop: 6 }} open={!!d.lauf || !!stand?.fehler}>
              <summary className="klein">Protokoll der Datenbanksicherung (letzte {d.protokoll.length} Zeilen)</summary>
              <div className="klein" style={{ fontFamily: 'var(--mono, monospace)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}>
                {d.protokoll.map((z: string, i: number) => <div key={i} style={{ color: /FEHLER/.test(z) ? 'var(--rot, #a33)' : undefined }}>{z}</div>)}
              </div>
            </details>
          )}
        </div>
      )}
      {!!d?.liste?.length && (
        <details style={{ marginTop: 10 }}>
          <summary className="klein">Datenbanksicherungen auf dem Server zum Herunterladen ({d.liste.length})</summary>
          <table className="liste" style={{ marginTop: 6 }}>
            <thead><tr><th>Datei</th><th>Stand</th><th style={{ textAlign: 'right' }}>Größe</th><th></th></tr></thead>
            <tbody>
              {d.liste.map((s: any) => (
                <tr key={s.name} style={{ cursor: 'default' }}>
                  <td><code>{s.name}</code></td>
                  <td>{zeitpunkt(s.am)}</td>
                  <td style={{ textAlign: 'right' }}>{mb(s.groesse)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <a className="knopf klein" href={`/api/sicherung/${encodeURIComponent(s.name)}`} download={s.name}>Herunterladen</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      <p className="klein leise" style={{ marginTop: 10 }}>
        Wiederherstellen auf einem neuen Server: das neueste Paket <code>mds_sicherung_….tar</code> und den Ordner „Dateiablage“ von der QNAP holen, auspacken und <code>bash wiederherstellen.sh --name ftth.mds-innovation.com --dateien /root/Dateiablage</code> (Anleitung liegt im Paket).
        Nur die Datenbank: <code>docker compose exec -T datenbank pg_restore -U mds -d mds --clean --if-exists</code> mit der Datei als Eingabe.
      </p>
    </Karte>
  );
}
